import { addSeconds, format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import type { SubscriptionUsageMeter } from 'features/subscription/services/subscription-service';
import { UsageBar } from 'features/usage/components/usage-bar';
import { UsageCreditCard } from 'features/usage/components/usage-credit-card';
import { formatCreditValue } from 'features/usage/components/usage-credits';
import { resetTime, UsageWindowLimits } from 'features/usage/components/usage-window-limit';
import type { WindowUsage } from 'features/usage/services/usage-service';
import { formatPercent, formatWindowName } from 'features/usage/utils/usage-format';
import { TruncateTooltip } from 'hooks/use-truncate-tooltip';
import { cn } from 'lib/utils';

const usedUpValueClassName = 'text-error-700 dark:text-error-500';

/** The daily window, or the first one when a plan has no daily window. */
export function mainWindow(windows: WindowUsage[]) {
  return windows.find((item) => item.type === 'daily') ?? windows[0];
}

export function meterPercent(meter: SubscriptionUsageMeter) {
  return meter.included > 0 ? (meter.used / meter.included) * 100 : 0;
}

/** A period ending at 23:59:59 renews the next day; one ending at midnight renews that day. */
export function meterRenewsOn(meter: SubscriptionUsageMeter) {
  return format(addSeconds(new Date(meter.periodEndUtc), 1), 'MMM d, yyyy');
}

function percentClassName(percent: number) {
  if (percent >= 100) return 'text-error-700 dark:text-error-500';
  if (percent >= 80) return 'text-warning-high-emphasis';
  return 'text-grey-800 dark:text-grey-300';
}

/** Figma 10827:13802 — bar with its percent at the right; red once used up, amber from 80%. */
function LimitBar({ percent }: { percent: number }) {
  return (
    <div className="flex items-center gap-4">
      <div className="min-w-0 flex-1">
        <UsageBar percent={percent} danger={percent >= 100} />
      </div>
      <span
        className={cn(
          'shrink-0 text-right text-xs font-medium leading-heading tabular-nums',
          percentClassName(percent)
        )}
      >
        {`${Math.round(Math.min(100, percent))}%`}
      </span>
    </div>
  );
}

/** Today's limit. The window API gives a percentage only, so the value is the share left. */
export function TeamsTodayCard({ windows }: { windows: WindowUsage[] }) {
  const { t } = useTranslation();
  const limitWindow = mainWindow(windows);
  if (!limitWindow) return null;

  const isDaily = limitWindow.type === 'daily';
  const when = limitWindow.resets_at
    ? resetTime(limitWindow.resets_at, t('TOMORROW', { defaultValue: 'tomorrow' }))
    : '';

  const getFooter = () => {
    if (when) return t('WINDOW_RESETS', { when, defaultValue: 'Resets {{when}}' });
    if (isDaily) return t('USAGE_RESETS_DAILY', { defaultValue: 'Resets every day' });
    return t('USAGE_RESETS_HOURLY', { defaultValue: 'Resets every hour' });
  };

  return (
    <UsageCreditCard
      title={isDaily ? t('TODAY', { defaultValue: 'Today' }) : formatWindowName(limitWindow.name, t)}
      value={formatPercent(Math.max(0, 100 - limitWindow.percent), 2)}
      valueClassName={limitWindow.percent >= 100 ? usedUpValueClassName : undefined}
      unit={t('USAGE_LEFT', { defaultValue: 'left' })}
      footer={getFooter()}
    >
      <LimitBar percent={limitWindow.percent} />
      <UsageWindowLimits windows={windows.filter((item) => item !== limitWindow)} />
    </UsageCreditCard>
  );
}

/** This billing period's AI credits from the person's seat. */
export function TeamsMonthCard({ meter }: { meter: SubscriptionUsageMeter }) {
  const { t } = useTranslation();
  const percent = meterPercent(meter);

  return (
    <UsageCreditCard
      title={t('TEAMS_THIS_MONTH', { defaultValue: 'This month' })}
      value={formatCreditValue(meter.remaining, meter.quantityScale)}
      valueClassName={percent >= 100 ? usedUpValueClassName : undefined}
      unit={t('USAGE_UNIT_LEFT', { unit: meter.unitLabel, defaultValue: '{{unit}} left' })}
      footer={t('USAGE_RENEWS_ON', { date: meterRenewsOn(meter), defaultValue: 'Renews on {{date}}' })}
    >
      <LimitBar percent={percent} />
      <TruncateTooltip
        text={t('CREDITS_USED_OF_INCLUDED', {
          used: formatCreditValue(meter.used, meter.quantityScale),
          included: formatCreditValue(meter.included, meter.quantityScale),
          unit: meter.unitLabel,
          defaultValue: '{{used}} of {{included}} {{unit}} used',
        })}
        className="text-xs text-muted-foreground"
      />
    </UsageCreditCard>
  );
}
